import {
	SUBSCRIPTION_TYPES,
	THIRD_PARTY_EVENTS
} from '$lib/utils/constants';
import { compare } from 'compare-versions';

/************************************************
 *************** CONSTANTS **********************
 *************************************************/
export const LOG_EVENT_TOKENS = {
	NMHP_LOAD: 'yayo86',
	NMHP_LOAD_AMAZON: 'ph9b9z', // this event is specific for firebase + Amazon
	SUB_PAGE_LOAD: '87ncx9',
	SUB_PAGE_LOAD_AMAZON: '6hvoqq',
	DEFAULT_PURCHASE_REVENUE_EVENT: 'wiyrko',
	AMAZON_SUBSCRIPTION: 'rvsr1g',
	AMAZON_REACTIVATION: 'gi600j',
	DEFAULT_PROSPECT_REGISTER: '503p8k',
	SUBSCRIPTION: 'byuafh',
	REACTIVATION: 'xqmv16',
	// Decomission list
	DEFAULT_LOGIN: '1kjd9f',
	AMAZON_LOGIN: 'gwbkpa'
};

/**
 * A mapping of page paths to their respective event tokens.
 * @type {{ [key:string]: { AMAZON: string; DEFAULT: string; }}} PAGE_MAP
 */
const PAGE_MAP = {
	'/': {
		AMAZON: LOG_EVENT_TOKENS.NMHP_LOAD_AMAZON,
		DEFAULT: LOG_EVENT_TOKENS.NMHP_LOAD
	},
	'/subscription': {
		AMAZON: LOG_EVENT_TOKENS.SUB_PAGE_LOAD_AMAZON,
		DEFAULT: LOG_EVENT_TOKENS.SUB_PAGE_LOAD
	}
};

const SUB_EVENT_MAP = {
	[SUBSCRIPTION_TYPES.NEW_SUBSCRIPTION]: {
		AMAZON: LOG_EVENT_TOKENS.AMAZON_SUBSCRIPTION,
		DEFAULT: LOG_EVENT_TOKENS.SUBSCRIPTION
	},
	[SUBSCRIPTION_TYPES.EXISTING_SUBSCRIPTION]: {
		AMAZON: LOG_EVENT_TOKENS.AMAZON_REACTIVATION,
		DEFAULT: LOG_EVENT_TOKENS.AMAZON_SUBSCRIPTION
	}
};

const LOGIN_EVENT_MAP = (/** @type {boolean} */ firstTimeLogin) => {
	// TODO: need new tokens for first time login
	if (firstTimeLogin)
		return {
			AMAZON: LOG_EVENT_TOKENS.AMAZON_LOGIN,
			DEFAULT: LOG_EVENT_TOKENS.DEFAULT_LOGIN
		};
	else
		return {
			AMAZON: LOG_EVENT_TOKENS.AMAZON_LOGIN,
			DEFAULT: LOG_EVENT_TOKENS.DEFAULT_LOGIN
		};
};

export const LOG_EVENT_TYPES = {
	ADJUST: 'adjust',
	FIREBASE: 'firebase'
};

export const appCallCallbacks = {
	ADJUST_EVENT: 'adjustCallback',
	FIREBASE_EVENT: 'firebaseCallback'
};

/**
 * @typedef {'subscribe' | 'pageLoad' | 'auxiliary' | 'upgrade' | 'login' | 'click' | 'appLoad'} EventType
 */
export const EventType = Object.freeze({
	Subscribe: 'subscribe',
	PageLoad: 'pageLoad',
	Auxiliary: 'auxiliary',
	Upgrade: 'upgrade',
	LogIn: 'login',
	Click: 'click',
	AppLoad: 'appLoad'
});

/************************************************
 *************** TRANSFORMERS *******************
 *************************************************/
/**
 * Transformer that filters out events that are not emitted in app.
 * @param {Array<import('./types').Events.Event<EventType, object>>} events - Events to filter.
 * @returns {Array<import('./types').Events.Event<EventType, object>>}
 */
export function filterInAppEvent(events) {
	const eventArray = Array.isArray(events) ? events : [events];
	return eventArray.filter((event) => event?.meta?.inApp !== false);
}

/**
 * @param {Array<import('./types').Events.Event<EventType, object>>} events incoming event
 * @returns {Array<object>}
 */
export function assignAdjust(events) {
	const eventArray = Array.isArray(events) ? events : [events];
	return eventArray.flatMap((eachEvent) => {
		const { meta, ...restOfEvent } = eachEvent;
		// TODO: add assertions instead of ?
		const appVersion = String(meta?.appVersion ?? '8.23.1');
		const isLegacyEvent = compare(appVersion, '8.23.0', '<=');
		const event = {
			...restOfEvent,
			request: appCallCallbacks.ADJUST_EVENT
		};
		if (isLegacyEvent) return event;
		return {
			...event,
			type: LOG_EVENT_TYPES.ADJUST
		};
	});
}

/**
 * @param {Array<import('./types').Events.Event<EventType, object>>} event incoming event
 * @returns {Array<object>}
 */
export function assignFirebase(event) {
	const eventArray = Array.isArray(event) ? event : [event];
	return eventArray.flatMap((eachEvent) => {
		// TODO: add assertions instead of ?
		const { meta, ...restOfEvent } = eachEvent;
		const appVersion = String(meta?.appVersion ?? '8.23.1');
		const isLegacyEvent = compare(appVersion, '8.23.0', '<=');
		const event = {
			...restOfEvent,
			request: appCallCallbacks.FIREBASE_EVENT
		};
		if (isLegacyEvent) return event;
		return {
			...event,
			type: LOG_EVENT_TYPES.FIREBASE
		};
	});
}

/**
 * @param {Array<object>} event incoming event
 * @param {import('./types').Events.SubscribeEvent} initialEvent incoming event
 * @returns {Array<object>}
 */
export function processSubscribeEvent(event, initialEvent) {
	const subType = initialEvent.detail.subscriptionType;
	const eventType = initialEvent.meta.isAmazonApp
		? SUB_EVENT_MAP[subType].AMAZON
		: SUB_EVENT_MAP[subType].DEFAULT;
	const eventArray = Array.isArray(event) ? event : [event];
	return eventArray.flatMap((_eachEvent) => {
		return [
			{
				meta: initialEvent.meta,
				event: eventType,
				customParameters: JSON.stringify({
					device_id: initialEvent.deviceId,
					legacy_track_cookie: initialEvent.legacyTrackCookie || 'none',
					family_ID: initialEvent.detail.userToProductId,
					udid: initialEvent.detail.udid,
					adid: initialEvent.detail.adid
				})
			}
		];
	});
}

/**
 * @param {Array<import('./types').Events.SubscribeEvent>} event incoming event
 * @param {import('./types').Events.SubscribeEvent} initialEvent incoming event
 * @returns {Array<object>}
 */
export function appendRevenueEvent(event, initialEvent) {
	if (initialEvent.detail.subscriptionType === SUBSCRIPTION_TYPES.EXISTING_SUBSCRIPTION)
		return event;
	const eventArray = Array.isArray(event) ? event : [event];
	return eventArray.flatMap((eachEvent) => {
		return [
			...event,
			{
				meta: eachEvent.meta,
				event: LOG_EVENT_TOKENS.DEFAULT_PURCHASE_REVENUE_EVENT,
				customParameters: JSON.stringify({
					device_id: initialEvent.deviceId,
					legacy_track_cookie: initialEvent.legacyTrackCookie || 'none',
					family_ID: initialEvent.detail.userToProductId,
					product_hash: initialEvent.detail.productHash,
					udid: initialEvent.detail.udid,
					adid: initialEvent.detail.adid
				}),
				feature: JSON.stringify({
					'event.setRevenue': {
						value: initialEvent.detail.ltv
							? initialEvent.detail.ltv
							: initialEvent.detail.productPrice,
						currency: initialEvent.detail.productCurrency
					}
				})
			}
		];
	});
}

/**
 * @param {Array<import('./types').Events.SubscribeEvent>} event incoming event
 * @param {import('./types').Events.SubscribeEvent} _initialEvent incoming event
 * @returns {Array<object>}
 */
export function processRevenueEvent(event, _initialEvent) {
	const eventArray = Array.isArray(event) ? event : [event];
	return eventArray.flatMap((eachEvent) => {
		return [
			{
				meta: eachEvent.meta,
				event: LOG_EVENT_TOKENS.DEFAULT_PURCHASE_REVENUE_EVENT,
				customParameters: JSON.stringify({
					device_id: eachEvent.deviceId,
					legacy_track_cookie: eachEvent.legacyTrackCookie,
					family_ID: eachEvent.detail.userToProductId,
					product_hash: eachEvent.detail.productHash,
					udid: eachEvent.detail.udid,
					adid: eachEvent.detail.adid
				}),
				feature: JSON.stringify({
					'event.setRevenue': {
						value: eachEvent.detail.productPrice,
						currency: eachEvent.detail.productCurrency
					}
				})
			}
		];
	});
}

/**
 * @param {Array<import('./types').Events.LoadEvent>} event incoming event
 * @param {Array<import('./types').Events.LoadEvent>} _initialValue incoming event
 * @returns {Array<object>}
 */
export function filterAndTransportForCorePages(event, _initialValue) {
	const eventArray = Array.isArray(event) ? event : [event];
	const PAGES_TRACKED_BY_EXTERNAL_SRC = Object.keys(PAGE_MAP);
	return eventArray
		.filter((eachEvent) => PAGES_TRACKED_BY_EXTERNAL_SRC.includes(eachEvent.detail.pageName))
		.map((eachPage) => ({
			meta: eachPage.meta,
			event: eachPage.meta.isAmazonApp
				? PAGE_MAP[eachPage.detail.pageName].AMAZON
				: PAGE_MAP[eachPage.detail.pageName].DEFAULT,
			customParameters: JSON.stringify({
				device_id: eachPage.deviceId,
				legacy_track_cookie: eachPage.legacyTrackCookie || 'none',
				family_ID: 0,
				udid: eachPage.detail.udid,
				adid: eachPage.detail.adid
			})
		}));
}

/**
 * @param {Array<import('./types').Events.LogInEvent>} event incoming event
 * @param {Array<import('./types').Events.LogInEvent>} _initialEvent of incoming event
 * @returns {Array<object>}
 */
export function processLoginEvent(event, _initialEvent) {
	const eventArray = Array.isArray(event) ? event : [event];
	return eventArray.map((eachEvent) => ({
		meta: eachEvent.meta,
		event: eachEvent.meta.isAmazonApp
			? LOGIN_EVENT_MAP(eachEvent.detail.firstTimeLogin).AMAZON
			: LOGIN_EVENT_MAP(eachEvent.detail.firstTimeLogin).DEFAULT,
		customParameters: JSON.stringify({
			device_id: eachEvent.deviceId,
			legacy_track_cookie: eachEvent.legacyTrackCookie || 'none',
			family_ID: eachEvent.detail.userToProductId,
			udid: eachEvent.detail.udid,
			adid: eachEvent.detail.adid
		})
	}));
}

/**
 * Processes iOS-specific purchase events (subscribe/upgrade) for server-side Adjust tracking.
 * Only generates events if the platform is iOS, otherwise returns empty array.
 * Returns only the SUBSCRIBE event with full API payload structure.
 * @param {import('./types').Events.SubscribeEvent | import('./types').Events.UpgradeEvent} event incoming event
 * @param {import('./types').Events.SubscribeEvent | import('./types').Events.UpgradeEvent} initialEvent initial event with full context
 * @returns {Array<object>}
 */
export function processSSAdjustPurchaseEvent(event, initialEvent) {
	const countryCode = initialEvent.meta?.viewerCountry || 'US';

	return [
		{
			conversion_event_id: initialEvent.detail?.conversionEventId || '',
			country_code: countryCode,
			hashed_email: '',
			user_agent: '',
			event_info: {
				product_hash: initialEvent.detail?.productHash || '',
				device_id: initialEvent.deviceId || '',
				legacy_track_cookie: initialEvent.legacyTrackCookie || '',
				udid: initialEvent.detail?.udid || '',
				adid: initialEvent.detail?.adid || '',
				event: 'szluwh' 
			},
			event_source: [LOG_EVENT_TYPES.ADJUST],
			event_type: THIRD_PARTY_EVENTS.SUBSCRIBE,
			event_source_url: '',
			user_to_product_uuid: initialEvent.detail?.userToProductId || ''
		}
	];
}

/**
 * Processes iOS-specific subscribe events for server-side Adjust tracking.
 * @param {import('./types').Events.SubscribeEvent} event incoming event
 * @param {import('./types').Events.SubscribeEvent} initialEvent initial event with full context
 * @returns {Array<object>}
 */
export function processSSAdjustSubscribeEvent(event, initialEvent) {
	return processSSAdjustPurchaseEvent(event, initialEvent);
}

/**
 * Processes iOS-specific upgrade events for server-side Adjust tracking.
 * @param {import('./types').Events.UpgradeEvent} event incoming event
 * @param {import('./types').Events.UpgradeEvent} initialEvent initial event with full context
 * @returns {Array<object>}
 */
export function processSSAdjustUpgradeEvent(event, initialEvent) {
	return processSSAdjustPurchaseEvent(event, initialEvent);
}

/**
 * Processes iOS-specific page load events for server-side Adjust tracking.
 * Only generates INITIATE_CHECKOUT event if the platform is iOS and page is subscription page.
 * Returns empty array for non-iOS platforms or non-subscription pages.
 * @param {import('./types').Events.LoadEvent} event incoming event
 * @param {import('./types').Events.LoadEvent} initialEvent initial event with full context
 * @returns {Array<object>}
 */
export function processSSAdjustPageLoadEvent(event, initialEvent) {
	const pageName = initialEvent.detail?.pageName || '';

	const countryCode = initialEvent.meta?.viewerCountry || 'US';

	return [
		{
			conversion_event_id: '',
			country_code: countryCode,
			hashed_email: '',
			user_agent: '',
			event_info: {
				device_id: initialEvent.deviceId || '',
				legacy_track_cookie: initialEvent.legacyTrackCookie || '',
				udid: initialEvent.detail?.udid || '',
				adid: initialEvent.detail?.adid || '',
				event: 'z0s8tt',
				page_name: pageName
			},
			event_source: [LOG_EVENT_TYPES.ADJUST],
			event_type: THIRD_PARTY_EVENTS.INITIATE_CHECKOUT,
			event_source_url: pageName,
			user_to_product_uuid: ''
		}
	];
}
